import type { ComponentType } from "react";
import type { MatchMode } from "@/components/match/match-mode";
import type { Msg } from "./project-mock-data";
import type { ProjectActivityItem } from "./ProjectWorkspace";
import type { DashboardData, DashboardEntity, DashboardSource } from "./use-dashboard-source";
import { ListingFacetView } from "@/pages/share/listings/ListingFacetView";
import { ListingHeaderMeta } from "@/pages/share/listings/ListingHeaderMeta";
import { ListingOverviewFields } from "@/pages/share/listings/ListingOverviewFields";
import { ListingSettingsModal } from "@/pages/share/listings/ListingSettingsModal";
import { ProjectDashboardSettings } from "./ProjectDashboardSettings";
import { ProjectFacetView } from "./ProjectFacetView";
import { ProjectOverviewFields } from "./ProjectOverviewFields";

// Ein Dashboard für jede Art von Projekt. genesis kennt Projekte verschiedener Art (`project.kind`);
// sie teilen Kopf, Übersicht, Reiter, Autosave und Löschen, unterscheiden sich aber darin, welche
// Flächen es gibt und was in der Übersicht steht. Diese Datei ist der eine Ort, der das je Art
// festlegt (Strategy): die Hülle (ProjectDashboard) fragt nur hier nach und kennt keine Art beim Namen.
//
// Eine Art bekommt nur die Flächen, die sie wirklich trägt. Eine Fläche, die bei einer Art nichts
// bedeuten würde, fehlt dort, statt leer oder abgeschaltet dazustehen.

export type DashboardKind = "PROJECT" | "LISTING";

/**
 * Die Flächen aller Arten. ACHTUNG, Schlüssel und Beschriftung gehen auseinander, und das ist
 * Absicht: `team` heißt im UI "Anforderungen", `mitglieder` heißt "Team", `aufteilen` heißt "Ablauf"
 * und `ablauf` heißt "Aktivität". Die Schlüssel stehen als gespeicherter Bestand in `data.sections`;
 * sie umzubenennen ließe bei jedem vorhandenen Projekt eine Fläche verschwinden.
 */
export type DashboardFacetKey =
  | "aufteilen"
  | "team"
  | "dokumente"
  | "mitglieder"
  | "kandidaten"
  | "bilder"
  | "bewerbungen"
  | "ablauf";

export interface DashboardFacet {
  label: Msg;
  /** Beschriftung der Pille, die die Fläche holt. Ohne Angabe steht dort ihr Name. */
  addLabel?: Msg;
  /** Braucht eine Quelle, die Matches verknüpfen kann; ohne `linkMatch` bleibt die Fläche verborgen. */
  needsMatchLinks?: boolean;
}

export const DASHBOARD_FACETS: Record<DashboardFacetKey, DashboardFacet> = {
  // Heißt "Ablauf", nicht "Aufteilung": hier stehen Pakete, Aufgaben und Termine in ihrer
  // zeitlichen Ordnung. Aufteilen ist die Tätigkeit, der Ablauf das Ergebnis.
  aufteilen: {
    label: { id: "project.facet.aufteilen.label", defaultMessage: "Ablauf" },
    addLabel: { id: "project.facet.aufteilen.add", defaultMessage: "Ablauf planen" },
  },
  team: {
    label: { id: "project.facet.team.label", defaultMessage: "Anforderungen" },
    addLabel: { id: "project.facet.team.add", defaultMessage: "Anforderungen hinzufügen" },
  },
  // Eigene Fläche statt eines Abschnitts in der Übersicht: Unterlagen sind der Stoff, aus dem
  // später der Kontext für den Assistenten wird, und die Übersicht soll kurz bleiben.
  dokumente: {
    label: { id: "project.facet.dokumente.label", defaultMessage: "Dokumente" },
    addLabel: { id: "project.facet.dokumente.add", defaultMessage: "Dokumente hinzufügen" },
  },
  // Die Menschen SELBST stehen hier; `kandidaten` ist der Weg, wie man an sie kommt.
  mitglieder: {
    label: { id: "project.facet.mitglieder.label", defaultMessage: "Team" },
    addLabel: { id: "project.facet.mitglieder.add", defaultMessage: "Team anzeigen" },
  },
  kandidaten: {
    label: { id: "project.facet.kandidaten.label", defaultMessage: "Match" },
    addLabel: { id: "project.facet.kandidaten.add", defaultMessage: "Menschen suchen" },
    needsMatchLinks: true,
  },
  bilder: {
    label: { id: "project.facet.bilder.label", defaultMessage: "Bilder" },
    addLabel: { id: "project.facet.bilder.add", defaultMessage: "Bilder hinzufügen" },
  },
  bewerbungen: {
    label: { id: "project.facet.bewerbungen.label", defaultMessage: "Bewerbungen" },
  },
  // Was im Projekt geschieht, nicht was geplant ist: der Plan lebt im Ablauf, hier sein Verlauf.
  ablauf: {
    label: { id: "project.facet.ablauf.label", defaultMessage: "Aktivität" },
    addLabel: { id: "project.facet.ablauf.add", defaultMessage: "Aktivität verfolgen" },
  },
};

/** Was eine Fläche, die Übersicht und die Einstellungen vom Dashboard bekommen. */
export interface DashboardContext {
  /** Die Art. Eine Fläche liest hier, was je Art verschieden ist, statt die Tabelle selbst zu importieren. */
  config: KindConfig;
  entity: DashboardEntity;
  source: DashboardSource;
  /** Titel und Beschreibung, wie sie gerade getippt sind, nicht wie zuletzt gespeichert. */
  title: string;
  description: string;
  data: DashboardData;
  /** Nur ansehen (`actions.canUpdate`): Werte als Text, keine Pillen. */
  readOnly: boolean;
  activity: readonly ProjectActivityItem[];
  /** Schreibt einen Ausschnitt des data-Dokuments: lokal sofort, gespeichert gebündelt. */
  editData: (patch: DashboardData) => void;
  /** Speichert offene Änderungen vor einem abhängigen KI-Aufruf; Fehler brechen den Aufruf ab. */
  flush: () => Promise<void>;
  /** Nimmt Flächen dauerhaft ins Projekt auf (`data.sections`), sobald in ihnen etwas entstanden ist. */
  keepFacets: (keys: readonly DashboardFacetKey[]) => void;
  /** Wechselt auf eine Fläche und sagt ihr, welche Ansicht sich öffnen soll. */
  openFacet: (key: DashboardFacetKey, view?: string | null) => void;
  log: (id: string, message: Msg) => void;
}

export interface FacetViewProps {
  facet: DashboardFacetKey;
  ctx: DashboardContext;
  /** Welche Ansicht in der Fläche offen ist; liegt beim Dashboard, damit ein Reiterwechsel sie hält. */
  view: string | null;
  onViewChange: (next: string | null) => void;
}

export interface SettingsProps {
  ctx: DashboardContext;
  /** Titel und Beschreibung gehen über das Dashboard, damit nur einer sie schreibt (Autosave). */
  onSaveBasics: (next: { title: string; description: string; data?: DashboardData }) => Promise<void>;
  onClose: () => void;
}

export interface KindTexts {
  badge: Msg;
  untitled: Msg;
  renameLabel: Msg;
  readOnlyBanner: Msg;
  settingsTitle: Msg;
  notFound: Msg;
  deleteTitle: Msg;
  deleteConfirm: Msg;
  /** Die leere Aktivität, in der Übersicht und auf ihrer Fläche. */
  activityEmpty: Msg;
}

/** Die Anlegen-Maske der Art (ProjectCreateScreen). */
export interface KindCreateTexts {
  heading: Msg;
  /** Die Zeile "SHARE · Erstellen" über der Überschrift; neue Seiten bekommen sie nicht mehr. */
  eyebrow: boolean;
  sub: Msg;
  titlePlaceholder: Msg;
  descPlaceholder: Msg;
  submit: Msg;
  /** Der Block "Kontext mitgeben" mit Unterlagen, Anbindungen und Generieren. */
  withContext: boolean;
}

// Die drei Modi, die Menschen suchen (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4).
// RANK_COMPANIES fehlt bewusst: der Unternehmens-Arm sucht Unternehmen statt Personen und liest weder
// Team-Profil noch Fachkompetenz-Leiter, die von hier mitgehen; ihn anzubieten hieße, eine Übernahme zu
// versprechen, die drüben nichts vorfindet.
export type ToMatchMode = Extract<MatchMode, "OPTIMIZE" | "ASSESS" | "RANK" | "RANK_COMPANIES">;

/** Was aus dem Objekt ins neue Match mitgehen kann. */
export type ToMatchTake = "description" | "requirements" | "scorecard" | "documents" | "people";

/** Die Fläche "Match" (ProjectCandidatesScreen): verknüpfte Matches und der Weg in ein neues. */
export interface KindMatchFacetTexts {
  /** Die Karte "Ich will matchen" unter "Menschen suchen". */
  wayDesc: Msg;
  linkedTitle: Msg;
  noLinked: Msg;
  unlinkTitle: Msg;
  /** Bekommt den Namen des Matches als `name`. */
  unlinkBody: Msg;
}

/** Der Weg "Ich will matchen" (ProjectToMatchScreen): Texte, angebotene Fragen und was mitgeht. */
export interface KindToMatch {
  sub: Msg;
  copyNote: Msg;
  /** Angelegt, ließ sich aber weder verknüpfen noch wieder löschen. */
  orphaned: Msg;
  /**
   * Angelegt und verknüpft, aber nicht alles übernommen oder eingeschaltet. `missing` ist people,
   * documents oder autoMatch, wenn genau eines fehlt, sonst several.
   */
  partial: Msg;
  /** Die angebotenen Fragen. Die erste ist vorausgewählt; steht nur eine da, ist sie fest. */
  modes: readonly [ToMatchMode, ...ToMatchMode[]];
  /** Die Zeilen unter "Was mitgeht", in ihrer Reihenfolge. */
  takes: readonly ToMatchTake[];
  requiredTakes?: readonly ToMatchTake[];
  /** Die Zeile `people`: wer bei dieser Art mitgehen kann. `detail` bekommt `count` (geht mit) und `total` (angeboten). */
  people: { label: Msg; detail: Msg };
  /**
   * Unter der Fragen-Auswahl der Schalter "Neue Bewerbungen automatisch bewerten", standardmäßig an;
   * angelegt wird dann nach dem Verknüpfen mit `setMatchAutoMatch`
   * (@external-adr GENESIS-0086-a-listing-ranking-belongs-to-an-immutable-match-version). Nur für eine Art, die
   * Bewerbungen bekommt, und nur bei RANK: genesis bewertet in keinem anderen Modus automatisch.
   */
  autoMatch: boolean;
  aiEnabledByDefault?: boolean;
}

export interface KindConfig {
  /** Name des Routen-Parameters mit der Id. */
  idParam: string;
  texts: KindTexts;
  create: KindCreateTexts;
  matchFacet: KindMatchFacetTexts;
  toMatch: KindToMatch;
  /** Die Flächen der Art, in der Reihenfolge der Reiter und Pillen. */
  facets: readonly DashboardFacetKey[];
  facetAddLabels?: (data: DashboardData) => Partial<Record<DashboardFacetKey, Msg>>;
  /** Immer da, ohne Pille: sie ergeben sich aus dem bloßen Bestehen, nicht aus einer Entscheidung. */
  alwaysFacets: readonly DashboardFacetKey[];
  /** Nur für Eingetragene (`entity.isMember`). */
  memberOnlyFacets: readonly DashboardFacetKey[];
  /**
   * Die Schlüssel, die das Dashboard in `data` schreiben darf. Alles andere geht nicht hinaus, auch
   * wenn eine Fläche es versucht: bei manchen Arten lesen auch Nicht-Mitglieder `data`.
   */
  dataKeys: readonly string[];
  /** Der Knopf für Anbindungen (Jira, Asana, …) neben den Pillen. */
  showServices: boolean;
  afterDelete: { path: string; replace: boolean };
  outsideReaderPath?: (id: string) => string;
  outsideReaderFeature?: string;
  /** Ob das Zahnrad erscheint. */
  settingsAvailable: (entity: DashboardEntity) => boolean;
  OverviewFields: ComponentType<{ ctx: DashboardContext; onOpenSettings?: () => void }>;
  FacetView: ComponentType<FacetViewProps>;
  Settings: ComponentType<SettingsProps>;
  /** Eine Angabe im Kopf auf der Zeile von "Angelegt am", etwa die Kennziffer einer Ausschreibung. */
  HeaderMeta?: ComponentType<{ ctx: DashboardContext }>;
}

export const DASHBOARD_KINDS: Record<DashboardKind, KindConfig> = {
  PROJECT: {
    idParam: "projectId",
    texts: {
      badge: { id: "project.workspace.typeBadge", defaultMessage: "Projekt" },
      untitled: { id: "project.workspace.untitled", defaultMessage: "Unbenanntes Projekt" },
      renameLabel: { id: "project.workspace.renameLabel", defaultMessage: "Projekt umbenennen" },
      readOnlyBanner: {
        id: "project.readOnlyBanner",
        defaultMessage: "Du kannst dieses Projekt nur ansehen. Bitte die Eigentümer:in um Schreibrechte.",
      },
      settingsTitle: { id: "project.settings.title", defaultMessage: "Projekt-Einstellungen" },
      notFound: { id: "project.notFound", defaultMessage: "Projekt nicht gefunden" },
      deleteTitle: { id: "project.delete.title", defaultMessage: "Projekt löschen" },
      deleteConfirm: {
        id: "project.delete.confirm",
        defaultMessage: "Dieses Projekt endgültig löschen? Verknüpfte Matches bleiben bestehen.",
      },
      activityEmpty: {
        id: "project.activity.empty",
        defaultMessage: "Noch nichts passiert. Hier landet, was am Projekt geschieht.",
      },
    },
    create: {
      heading: { id: "project.create.title", defaultMessage: "Neues Projekt" },
      eyebrow: true,
      sub: {
        id: "project.create.sub",
        defaultMessage: "Titel genügt. Alles Weitere ist freiwillig und lässt sich später im Projekt ergänzen.",
      },
      titlePlaceholder: { id: "project.create.titlePlaceholder", defaultMessage: "Wofür brauchst du Leute?" },
      descPlaceholder: {
        id: "project.create.descPlaceholder",
        defaultMessage: "Worum geht es? Zwei Sätze reichen, der Rest wächst mit dem Projekt.",
      },
      submit: { id: "project.create.submit", defaultMessage: "Projekt anlegen" },
      withContext: true,
    },
    matchFacet: {
      wayDesc: {
        id: "project.candidates.matchDesc",
        defaultMessage: "Direkt in MATCH, mit den Requirements aus diesem Projekt.",
      },
      linkedTitle: { id: "project.candidates.linkedTitle", defaultMessage: "Matches dieses Projekts" },
      noLinked: { id: "project.candidates.noLinked", defaultMessage: "Zu diesem Projekt gibt es noch kein Match." },
      unlinkTitle: { id: "project.candidates.unlinkTitle", defaultMessage: "Verbindung zum Projekt lösen" },
      unlinkBody: {
        id: "project.candidates.unlinkBody",
        defaultMessage:
          "„{name}“ gehört dann nicht mehr zu diesem Projekt. Das Match selbst bleibt mit Kandidaten und Ergebnissen erhalten.",
      },
    },
    toMatch: {
      sub: {
        id: "project.toMatch.sub",
        defaultMessage: "Das Match startet nicht bei null. Wähle, was aus dem Projekt mitgeht.",
      },
      copyNote: {
        id: "project.toMatch.copyNote",
        defaultMessage:
          "Übernommen wird eine Kopie: was du im Match änderst, ändert das Projekt nicht. Verbunden bleiben beide trotzdem, das Match erscheint danach im Projekt.",
      },
      orphaned: {
        id: "project.toMatch.orphaned",
        defaultMessage:
          "Das Match ist angelegt, ließ sich aber nicht mit dem Projekt verknüpfen. Du findest es unter MATCH.",
      },
      partial: {
        id: "project.toMatch.partial",
        defaultMessage:
          "Das Match ist angelegt und mit dem Projekt verknüpft, aber {missing, select, people {die Personen} documents {die Dokumente} other {die Personen und die Dokumente}} ließen sich nicht übernehmen. Du kannst sie im Match nachtragen.",
      },
      // Optimieren zuerst und damit vorausgewählt: ein Projekt sucht Menschen für seine Aufgaben.
      modes: ["OPTIMIZE", "ASSESS", "RANK"],
      takes: ["description", "requirements", "scorecard", "documents", "people"],
      // Ein Projekt bekommt keine Bewerbungen, also gibt es nichts automatisch zu bewerten.
      autoMatch: false,
      people: {
        label: { id: "project.toMatch.people", defaultMessage: "Personen" },
        detail: {
          id: "project.toMatch.peopleDetail",
          defaultMessage:
            "{count, plural, =0 {Noch niemand im Projekt} one {Eine Person aus dem Projekt} other {# Personen aus dem Projekt}}",
        },
      },
    },
    facets: ["aufteilen", "team", "dokumente", "mitglieder", "kandidaten", "ablauf"],
    alwaysFacets: ["ablauf"],
    memberOnlyFacets: [],
    dataKeys: [
      "sections",
      "status",
      "anlass",
      "tags",
      "packages",
      "events",
      "requirements",
      "candidates",
      "documents",
    ],
    showServices: true,
    afterDelete: { path: "/share", replace: false },
    // Das Zahnrad steht immer da; ohne Schreibrecht öffnet es die Einstellungen nur zum Lesen.
    settingsAvailable: () => true,
    OverviewFields: ProjectOverviewFields,
    FacetView: ProjectFacetView,
    Settings: ProjectDashboardSettings,
  },
  // Eine Ausschreibung ist ein Projekt der Art LISTING
  // (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item). Sie hat Inhalt
  // (`plan`), Dokumente, Kandidaten und Bewerbungen. Keine Projekt-Zeitachse, Gefragten, Anlass oder Tags.
  // Genesis entfernt interne Dokumentverweise aus `data` für Empfänger und Bewerbende.
  LISTING: {
    idParam: "listingId",
    texts: {
      badge: { id: "share.listings.dashboard.badge", defaultMessage: "Ausschreibung" },
      untitled: { id: "share.listings.dashboard.untitled", defaultMessage: "Unbenannte Ausschreibung" },
      renameLabel: { id: "share.listings.dashboard.rename", defaultMessage: "Ausschreibung umbenennen" },
      readOnlyBanner: {
        id: "share.listings.dashboard.readOnly",
        defaultMessage: "Du kannst diese Ausschreibung nur ansehen.",
      },
      settingsTitle: { id: "share.listings.settings.title", defaultMessage: "Mitglieder und Berechtigungen" },
      notFound: { id: "share.listings.notFound", defaultMessage: "Ausschreibung nicht gefunden" },
      deleteTitle: { id: "share.listings.delete", defaultMessage: "Ausschreibung löschen" },
      deleteConfirm: {
        id: "share.listings.deleteMessage",
        defaultMessage:
          "Löscht die Ausschreibung mit Mitgliedern, Leserschaft und Kategorien. Verknüpfte Matches bleiben bestehen.",
      },
      activityEmpty: {
        id: "share.listings.dashboard.activityEmpty",
        defaultMessage: "Noch nichts passiert. Hier landet, was an der Ausschreibung geschieht.",
      },
    },
    create: {
      heading: { id: "share.listings.create", defaultMessage: "Neue Ausschreibung" },
      eyebrow: false,
      sub: {
        id: "share.listings.createScreen.sub",
        defaultMessage:
          "Titel genügt. Frist, Bilder, Leserschaft und Mitglieder ergänzt du danach in der Ausschreibung.",
      },
      titlePlaceholder: { id: "share.listings.form.titlePlaceholder", defaultMessage: "Stelle, Programm oder Aufruf" },
      descPlaceholder: {
        id: "share.listings.createScreen.descPlaceholder",
        defaultMessage: "Was wird ausgeschrieben, und wen sucht ihr?",
      },
      submit: { id: "share.listings.createScreen.submit", defaultMessage: "Ausschreibung anlegen" },
      withContext: false,
    },
    matchFacet: {
      wayDesc: {
        id: "share.listings.matches.wayDesc",
        defaultMessage: "Direkt in MATCH, mit den Anforderungen aus dieser Ausschreibung.",
      },
      linkedTitle: { id: "share.listings.matches.linkedTitle", defaultMessage: "Matches dieser Ausschreibung" },
      noLinked: {
        id: "share.listings.matches.noLinked",
        defaultMessage: "Zu dieser Ausschreibung gibt es noch kein Match.",
      },
      unlinkTitle: { id: "share.listings.matches.unlinkTitle", defaultMessage: "Verbindung zur Ausschreibung lösen" },
      unlinkBody: {
        id: "share.listings.matches.unlinkBody",
        defaultMessage:
          "„{name}“ gehört dann nicht mehr zu dieser Ausschreibung. Das Match selbst bleibt mit Kandidaten und Ergebnissen erhalten.",
      },
    },
    toMatch: {
      sub: {
        id: "share.listings.toMatch.sub",
        defaultMessage: "Das Match startet nicht bei null. Wähle, was aus der Ausschreibung mitgeht.",
      },
      copyNote: {
        id: "share.listings.toMatch.copyNote",
        defaultMessage:
          "Fachkompetenzen und Powers kommen aus den Anforderungen dieser Ausschreibung. Geänderte Anforderungen werden mit einer neuen Match-Version übernommen.",
      },
      orphaned: {
        id: "share.listings.toMatch.orphaned",
        defaultMessage:
          "Das Match ist angelegt, ließ sich aber nicht mit der Ausschreibung verknüpfen. Du findest es unter MATCH.",
      },
      partial: {
        id: "share.listings.toMatch.partial",
        defaultMessage:
          "Das Match ist angelegt und mit der Ausschreibung verknüpft, aber {missing, select, people {die Bewerbenden ließen sich nicht übernehmen. Du kannst sie im Match nachtragen} autoMatch {die automatische Bewertung ließ sich nicht einschalten. Du kannst sie in den Match-Einstellungen einschalten} other {weder ließen sich die Bewerbenden übernehmen noch die automatische Bewertung einschalten. Du kannst beides im Match nachholen}}.",
      },
      // Eine Ausschreibung besetzt eine Stelle: die Frage ist, wer von den Bewerbenden am besten
      // passt, nicht welches Team entsteht.
      modes: ["RANK"],
      // Die Personen sind die Bewerbenden; interne Dokumente können zusätzlich mitgehen.
      takes: ["description", "requirements", "scorecard", "documents", "people"],
      requiredTakes: ["requirements", "scorecard"],
      autoMatch: true,
      aiEnabledByDefault: true,
      people: {
        label: { id: "share.listings.toMatch.applicants", defaultMessage: "Bewerbende" },
        detail: {
          id: "share.listings.toMatch.applicantsDetail",
          defaultMessage:
            "{total, plural, =0 {Keine Bewerbung, die eingegangen, in Prüfung oder angenommen ist} other {{count, plural, =0 {Niemand geht mit} one {Eine Person aus den Bewerbungen} other {# Personen aus den Bewerbungen}}}}",
        },
      },
    },
    // "kandidaten" zeigt die verknüpften Matches und den Weg in ein neues; holen darf die Fläche, wer
    // Matches verknüpfen darf (`actions.canManageMatches`).
    facets: ["team", "bilder", "dokumente", "kandidaten", "bewerbungen", "ablauf"],
    facetAddLabels: (data) => ({ kandidaten: data.searchTarget === "COMPANIES"
      ? { id: "share.listings.searchCompanies", defaultMessage: "Unternehmen suchen" }
      : DASHBOARD_FACETS.kandidaten.addLabel! }),
    alwaysFacets: ["bewerbungen", "ablauf"],
    // Bewerbungen beantwortet genesis nur Eingetragenen
    // (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §4)), Mitglieder und
    // Kandidaten liest es ebenfalls nur ihnen vor.
    memberOnlyFacets: ["dokumente", "kandidaten", "bewerbungen"],
    dataKeys: ["sections", "requirements", "matchRequirements", "documents", "searchTarget", "companyRequirements", "tags", "jobLocationId", "contractTypeId", "workingTimeModelId"],
    showServices: false,
    afterDelete: { path: "/share/listings", replace: true },
    outsideReaderPath: (id) => `/share/open-listings/${id}`,
    outsideReaderFeature: "share.applications",
    settingsAvailable: (entity) => entity.actions.canManageAccess,
    OverviewFields: ListingOverviewFields,
    FacetView: ListingFacetView,
    Settings: ListingSettingsModal,
    HeaderMeta: ListingHeaderMeta,
  },
};

/** Nur die Schlüssel, die die Art in `data` schreiben darf. */
export function allowedData(config: KindConfig, patch: DashboardData): DashboardData {
  return Object.fromEntries(Object.entries(patch).filter(([key]) => config.dataKeys.includes(key)));
}

/**
 * Die Flächen aus `data.sections`, die es bei dieser Art gibt. Unbekannte Schlüssel fliegen raus:
 * eine umbenannte oder entfallene Fläche soll keinen toten Reiter hinterlassen.
 */
export function storedSections(config: KindConfig, data: DashboardData): DashboardFacetKey[] {
  const raw = data.sections;
  if (!Array.isArray(raw)) return [];
  return raw.filter((key): key is DashboardFacetKey => config.facets.includes(key as DashboardFacetKey));
}

/** Die Flächen, die diese Person bei dieser Art überhaupt bekommen kann, in ihrer Reihenfolge. */
export function availableFacets(
  config: KindConfig,
  { isMember, canLinkMatches }: { isMember: boolean; canLinkMatches: boolean },
): DashboardFacetKey[] {
  return config.facets.filter(
    (key) =>
      (isMember || !config.memberOnlyFacets.includes(key)) &&
      (canLinkMatches || !DASHBOARD_FACETS[key].needsMatchLinks),
  );
}
